// admin/pages/Dashboard.js
import React, { useState, useEffect } from "react";
import {
  FaUsers,
  FaMapMarkerAlt,
  FaComments,
  FaChartBar,
  FaSitemap,
  FaBullhorn,
  FaUserShield,
  FaUsersCog,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaUserCheck,
  FaMale,
  FaFemale,
  FaSpinner
} from "react-icons/fa";

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to get token from all possible storage locations
  const getAuthToken = () => {
    // Check localStorage
    const localStorageToken = localStorage.getItem("token");
    if (localStorageToken) {
      console.log("Found token in localStorage");
      return localStorageToken;
    }
    
    // Check sessionStorage
    const sessionStorageToken = sessionStorage.getItem("token");
    if (sessionStorageToken) {
      console.log("Found token in sessionStorage");
      return sessionStorageToken;
    }
    
    // Check for different token names that might be used
    const possibleTokenNames = [
      "authToken",
      "access_token",
      "accessToken",
      "jwt",
      "jwtToken",
      "tinubu_token",
      "admin_token"
    ];
    
    for (const tokenName of possibleTokenNames) {
      const token = localStorage.getItem(tokenName) || sessionStorage.getItem(tokenName);
      if (token) {
        console.log(`Found token as ${tokenName}`);
        return token;
      }
    }
    
    // Check if token is in Auth Context or React state
    // You might need to get it from context if using AuthContext
    const authState = JSON.parse(localStorage.getItem("authState") || sessionStorage.getItem("authState") || "{}");
    if (authState.token) {
      console.log("Found token in authState");
      return authState.token;
    }
    
    console.log("No token found in any storage location");
    return null;
  };

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        console.log("Starting dashboard fetch...");
        
        const token = getAuthToken();
        if (!token) {
          throw new Error("No authentication token found. Please login again.");
        }

        console.log("Using token:", token.substring(0, 30) + "...");

        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 
                          process.env.REACT_APP_API_URL || 
                          "https://api.tinubu-ambode.org";
        
        console.log("API Base URL:", apiBaseUrl);
        
        const apiUrl = `${apiBaseUrl}/api/v1/analytics/dashboard/super-admin`;
        console.log("Full API URL:", apiUrl);

        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          credentials: 'include'
        });

        console.log("Response status:", response.status, response.statusText);

        if (response.status === 401) {
          // Clear invalid tokens
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          throw new Error("Session expired. Please login again.");
        }

        if (response.status === 403) {
          throw new Error("Access denied. You don't have permission to view this dashboard.");
        }

        if (!response.ok) {
          const errorText = await response.text();
          console.error("API Error Response:", errorText);
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log("API Response Data:", data);
        
        if (data.success) {
          setDashboardData(data.data);
          setError(null);
        } else {
          throw new Error(data.message || "API returned unsuccessful response");
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError(err.message);
        setDashboardData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const container = {
    width: "100%",
    minHeight: "100vh",
    fontFamily: "Inter, sans-serif",
    backgroundColor: "#f8fafc",
    padding: "0",
    margin: "0",
  };

  const contentWrapper = {
    width: "100%",
    maxWidth: "100%",
    padding: "15px",
    boxSizing: "border-box",
  };

  const header = {
    marginBottom: "25px",
    padding: "0 5px",
  };

  const title = {
    fontSize: "22px",
    fontWeight: "700",
    color: "#2563eb",
    marginBottom: "8px",
    lineHeight: "1.3",
  };

  const subtitle = {
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "1.5",
    margin: "0",
  };

  const statGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "15px",
    marginBottom: "25px",
    width: "100%",
  };

  const statCard = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    padding: "20px",
    border: "1px solid #e5e7eb",
    width: "100%",
    boxSizing: "border-box",
    minHeight: "140px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };

  const statTitle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#2563eb",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "10px",
  };

  const statValue = {
    fontSize: "24px",
    fontWeight: "700",
    color: "#111827",
    marginBottom: "6px",
  };

  const statSubValue = {
    fontSize: "14px",
    color: "#6b7280",
    fontWeight: "500",
  };

  const mainGrid = {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "20px",
    width: "100%",
  };

  const card = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "20px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    width: "100%",
    boxSizing: "border-box",
  };

  const sectionTitle = {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "18px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "12px",
    borderBottom: "2px solid #f1f5f9",
  };

  const sectionIcon = {
    color: "#2563eb",
    fontSize: "16px",
  };

  const performanceItem = {
    borderBottom: "1px solid #f8fafc",
    padding: "16px 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const performanceDetails = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  };

  const performanceName = {
    fontSize: "15px",
    fontWeight: "600",
    color: "#111827",
  };

  const performanceRole = {
    fontSize: "13px",
    color: "#6b7280",
  };

  const performanceMetric = {
    fontSize: "16px",
    fontWeight: "700",
    color: "#2563eb",
  };

  const distributionItem = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid #f8fafc",
  };

  const distributionLabel = {
    fontSize: "14px",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const distributionValue = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
  };

  const progressContainer = {
    height: "10px",
    backgroundColor: "#e5e7eb",
    borderRadius: "10px",
    overflow: "hidden",
    marginTop: "4px",
  };

  const progressBar = {
    height: "100%",
    borderRadius: "10px",
  };

  const loadingContainer = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    fontSize: "16px",
    color: "#6b7280",
    gap: "15px",
  };

  const errorContainer = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    padding: "20px",
    textAlign: "center",
    gap: "20px",
  };

  const errorIcon = {
    fontSize: "48px",
    color: "#ef4444",
  };

  const errorText = {
    color: "#ef4444",
    fontSize: "16px",
    maxWidth: "500px",
    lineHeight: "1.5",
  };

  const loginButton = {
    backgroundColor: "#2563eb",
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    textDecoration: "none",
  };

  const debugInfo = {
    backgroundColor: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "15px",
    marginTop: "20px",
    fontSize: "12px",
    color: "#6b7280",
    textAlign: "left",
    maxWidth: "500px",
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return "0";
    return num.toLocaleString();
  };

  const formatPercentage = (num) => {
    if (num === undefined || num === null) return "0%";
    return `${num.toFixed(2)}%`;
  };

  const calculatePercentage = (value, total) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "#f59e0b",
      approved: "#10b981",
      suspended: "#ef4444",
      rejected: "#6b7280"
    };
    return colors[status] || "#6b7280";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: FaClock,
      approved: FaCheckCircle,
      suspended: FaExclamationTriangle,
      rejected: FaExclamationTriangle
    };
    return icons[status] || FaUsers;
  };

  const handleLoginRedirect = () => {
    // Clear any existing auth data
    localStorage.clear();
    sessionStorage.clear();
    // Redirect to login
    window.location.href = "/login";
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    // Force re-fetch
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const responsiveStyles = `
    @media (min-width: 768px) {
      .dashboard-content-wrapper {
        padding: 25px;
        max-width: 1200px;
        margin: 0 auto;
      }
      
      .dashboard-stat-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        margin-bottom: 35px;
      }
      
      .dashboard-stat-card {
        padding: 25px;
        min-height: 160px;
      }
      
      .dashboard-stat-value {
        font-size: 28px;
      }
      
      .dashboard-main-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 25px;
      }
      
      .dashboard-card {
        padding: 25px;
      }
    }
    
    @media (min-width: 1200px) {
      .dashboard-stat-grid {
        grid-template-columns: repeat(6, 1fr);
      }
      
      .dashboard-main-grid {
        grid-template-columns: 2fr 1fr;
      }
    }
    
    @media (max-width: 767px) {
      .dashboard-stat-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    
    @media (max-width: 480px) {
      .dashboard-stat-grid {
        grid-template-columns: 1fr;
      }
    }
  `;

  if (loading) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={loadingContainer}>
            <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '32px' }} />
            <div>Loading dashboard data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={errorContainer}>
            <FaExclamationTriangle style={errorIcon} />
            <div style={errorText}>{error}</div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={loginButton} onClick={handleLoginRedirect}>
                Go to Login
              </button>
              <button style={{...loginButton, backgroundColor: '#6b7280'}} onClick={handleRetry}>
                Retry
              </button>
            </div>
            
            <div style={debugInfo}>
              <div style={{ fontWeight: '600', marginBottom: '10px' }}>Debug Information:</div>
              <div>Checking storage for tokens...</div>
              <div>LocalStorage token: {localStorage.getItem("token") ? "Found" : "Not found"}</div>
              <div>SessionStorage token: {sessionStorage.getItem("token") ? "Found" : "Not found"}</div>
              <div>Environment: {process.env.NODE_ENV}</div>
              <div>API URL: {process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "Not set"}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={errorContainer}>
            <FaExclamationTriangle style={errorIcon} />
            <div style={errorText}>No data available from API</div>
            <button style={loginButton} onClick={handleRetry}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { nationwide_summary, geographic_distribution, admin_performance, engagement, metadata } = dashboardData;

  return (
    <>
      <style>{responsiveStyles}</style>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      
      <div style={container} className="dashboard-container">
        <div style={contentWrapper} className="dashboard-content-wrapper">
          <div style={header}>
            <h2 style={title} className="dashboard-title">Nationwide Analytics Dashboard</h2>
            <p style={subtitle} className="dashboard-subtitle">
              {metadata?.jurisdiction?.description || "Monitor and manage your organization across all states"}
              {metadata?.generated_at && (
                <span> • Last updated: {new Date(metadata.generated_at).toLocaleString()}</span>
              )}
            </p>
          </div>

          <div style={statGrid} className="dashboard-stat-grid">
            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Total Members</span>
                <FaUsers color="#2563eb" />
              </div>
              <div style={statValue}>{formatNumber(nationwide_summary?.total_members)}</div>
              <div style={statSubValue}>
                {formatNumber(nationwide_summary?.by_status?.approved || 0)} approved
              </div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Pending Approvals</span>
                <FaClock color="#f59e0b" />
              </div>
              <div style={statValue}>{formatNumber(nationwide_summary?.by_status?.pending || 0)}</div>
              <div style={statSubValue}>
                Approval rate: {formatPercentage(nationwide_summary?.approval_rate)}
              </div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Active Users</span>
                <FaUserCheck color="#10b981" />
              </div>
              <div style={statValue}>{formatNumber(engagement?.active_users)}</div>
              <div style={statSubValue}>
                Nationwide coverage
              </div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Group Chats</span>
                <FaUsersCog color="#8b5cf6" />
              </div>
              <div style={statValue}>{formatNumber(engagement?.group_chats?.total_groups)}</div>
              <div style={statSubValue}>
                {formatNumber(engagement?.messages?.total_messages)} total messages
              </div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Announcements</span>
                <FaBullhorn color="#f59e0b" />
              </div>
              <div style={statValue}>{formatNumber(engagement?.announcements?.total_created)}</div>
              <div style={statSubValue}>
                {formatNumber(engagement?.announcements?.total_recipients)} recipients
              </div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Administrators</span>
                <FaUserShield color="#ef4444" />
              </div>
              <div style={statValue}>{formatNumber(admin_performance?.total_admins)}</div>
              <div style={statSubValue}>
                Managing {formatNumber(nationwide_summary?.total_members)} members
              </div>
            </div>
          </div>

          <div style={mainGrid} className="dashboard-main-grid">
            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaChartBar style={sectionIcon} />
                Top Performing Administrators
              </h3>

              {admin_performance?.top_performers?.length > 0 ? (
                admin_performance.top_performers.slice(0, 5).map((admin, index) => (
                  <div key={admin.admin_id} style={performanceItem} className="dashboard-performance-item">
                    <div style={performanceDetails}>
                      <div style={performanceName}>{admin.admin_name}</div>
                      <div style={performanceRole}>
                        {admin.admin_role} • {admin.jurisdiction?.description || admin.jurisdiction?.level}
                      </div>
                    </div>
                    <div style={performanceMetric}>
                      {admin.metrics?.members_in_jurisdiction} members
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ padding: "20px 0", textAlign: "center", color: "#6b7280" }}>
                  No administrator data available
                </div>
              )}
            </div>

            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaSitemap style={sectionIcon} />
                Member Status Distribution
              </h3>

              {nationwide_summary?.by_status && Object.entries(nationwide_summary.by_status).map(([status, count]) => {
                const StatusIcon = getStatusIcon(status);
                const percentage = calculatePercentage(count, nationwide_summary.total_members);
                
                return (
                  <div key={status} style={distributionItem} className="dashboard-distribution-item">
                    <div style={distributionLabel}>
                      <StatusIcon color={getStatusColor(status)} />
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </div>
                    <div style={distributionValue}>
                      {formatNumber(count)} ({percentage}%)
                    </div>
                  </div>
                );
              })}

              <div style={progressContainer}>
                {nationwide_summary?.by_status && Object.entries(nationwide_summary.by_status).map(([status, count]) => {
                  const percentage = calculatePercentage(count, nationwide_summary.total_members);
                  return (
                    <div
                      key={status}
                      style={{
                        ...progressBar,
                        width: `${percentage}%`,
                        backgroundColor: getStatusColor(status),
                        display: 'inline-block'
                      }}
                    />
                  );
                })}
              </div>
            </div>

            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaUsers style={sectionIcon} />
                Member Categories
              </h3>

              {nationwide_summary?.by_category && Object.entries(nationwide_summary.by_category).map(([category, count]) => (
                <div key={category} style={distributionItem} className="dashboard-distribution-item">
                  <div style={distributionLabel}>{category}</div>
                  <div style={distributionValue}>{formatNumber(count)}</div>
                </div>
              ))}

              {nationwide_summary?.by_gender && (
                <>
                  <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #e5e7eb" }}>
                    <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "15px", color: "#374151" }}>
                      Gender Distribution
                    </h4>
                    {Object.entries(nationwide_summary.by_gender).map(([gender, count]) => (
                      <div key={gender} style={distributionItem} className="dashboard-distribution-item">
                        <div style={distributionLabel}>
                          {gender === 'male' ? <FaMale color="#3b82f6" /> : <FaFemale color="#ec4899" />}
                          {gender.charAt(0).toUpperCase() + gender.slice(1)}
                        </div>
                        <div style={distributionValue}>{formatNumber(count)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaMapMarkerAlt style={sectionIcon} />
                Geographic Distribution
              </h3>

              {geographic_distribution?.distribution?.length > 0 ? (
                geographic_distribution.distribution.map((state, index) => (
                  <div key={state.code} style={distributionItem} className="dashboard-distribution-item">
                    <div style={distributionLabel}>{state.name}</div>
                    <div style={distributionValue}>{formatNumber(state.count)} members</div>
                  </div>
                ))
              ) : (
                <div style={{ padding: "20px 0", textAlign: "center", color: "#6b7280" }}>
                  No geographic data available
                </div>
              )}
            </div>

            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaBullhorn style={sectionIcon} />
                Engagement Metrics
              </h3>

              <div style={distributionItem} className="dashboard-distribution-item">
                <div style={distributionLabel}>Announcements Created</div>
                <div style={distributionValue}>{formatNumber(engagement?.announcements?.total_created)}</div>
              </div>

              <div style={distributionItem} className="dashboard-distribution-item">
                <div style={distributionLabel}>Total Recipients</div>
                <div style={distributionValue}>{formatNumber(engagement?.announcements?.total_recipients)}</div>
              </div>

              <div style={distributionItem} className="dashboard-distribution-item">
                <div style={distributionLabel}>Delivery Rate</div>
                <div style={distributionValue}>{formatPercentage(engagement?.announcements?.delivery_rate)}</div>
              </div>

              <div style={distributionItem} className="dashboard-distribution-item">
                <div style={distributionLabel}>Read Rate</div>
                <div style={distributionValue}>{formatPercentage(engagement?.announcements?.read_rate)}</div>
              </div>

              <div style={distributionItem} className="dashboard-distribution-item">
                <div style={distributionLabel}>Total Messages</div>
                <div style={distributionValue}>{formatNumber(engagement?.messages?.total_messages)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;